import { ParagonCrmProvider } from "components/integrations/managed_integrations/crmProviders";
import {
  MappableField,
  SaveFieldMappingRequestStotlesObjectTypeEnum,
} from "lib/generated/integration-api";
import {
  CrmRecordKind,
  LookupTargets,
  RECORD_FIELD_SPECS,
  RecordFieldSpec,
  resolveLookupTargets,
} from "./recordFields";

/**
 * Whether a CRM field can hold what an application field produces.
 *
 * Offering only the compatible ones is the point of configuring the mapping here rather than in
 * Paragon's own portal, which lists every field and lets a mapping be saved that the CRM rejects.
 * The account is the case that bites: a CRM refuses a record whose account was written to a text
 * column, and Dynamics' `accountidname` — a read-only computed field — reads like the right choice.
 *
 * The spec must already have its reference target resolved for the connected CRM, which is what
 * `recordFieldsFor` does — an unresolved one accepts nothing, since no field points at nowhere.
 */
export function acceptsCrmField(spec: RecordFieldSpec, field: MappableField): boolean {
  switch (spec.control) {
    case "account":
    case "owner":
      // A reference is bound to the referenced record's path, so the field has to point at the
      // collection this application field picks from.
      return (
        field.type === "lookup" &&
        !!spec.lookupEntitySet &&
        (field.targetEntitySets ?? []).includes(spec.lookupEntitySet)
      );
    case "picklist":
      // The value sent is one of the CRM's own option ids, which only an option set understands.
      return field.type === "enum";
    case "number":
      // A value carries pence, so a column that only takes whole numbers would reject it.
      return field.type === "number" && !field.integerOnly;
    case "date":
      return field.type === "date";
    case "string":
    case "textarea":
      return field.type === "string";
  }
}

/**
 * Whether a CRM object holds a person, which decides whether a record written to it may map the
 * person's details too. Read from the objects a *contact* may be mapped to rather than listed
 * again; the service derives it the same way and refuses a save that disagrees.
 */
export function holdsAPerson(provider: ParagonCrmProvider, entityName: string): boolean {
  return provider.mappableObjects.contact.some(
    (entity) => entity.toLowerCase() === entityName.toLowerCase(),
  );
}

/**
 * The application fields one record offers, in the order the form lists them, with each reference
 * pointed at what the connected CRM calls the records it references.
 *
 * The object matters as well as the record: a lead written to a CRM lead describes a person too,
 * and one written to an opportunity does not.
 */
export function recordFieldsFor(
  kind: CrmRecordKind,
  targets: LookupTargets,
  personFields: boolean,
): RecordFieldSpec[] {
  return resolveLookupTargets(
    RECORD_FIELD_SPECS.filter(
      (spec) => spec.kinds.includes(kind) && (personFields || !spec.aboutThePerson),
    ),
    targets,
  );
}

/**
 * The object type the service stores each record's mapping under. Typed against the generated enum
 * so a record that stops lining up with it is a build error rather than a mapping nothing finds.
 */
export const OBJECT_TYPE_BY_KIND: Record<
  CrmRecordKind,
  SaveFieldMappingRequestStotlesObjectTypeEnum
> = {
  lead: SaveFieldMappingRequestStotlesObjectTypeEnum.Lead,
  bid: SaveFieldMappingRequestStotlesObjectTypeEnum.Bid,
  contact: SaveFieldMappingRequestStotlesObjectTypeEnum.Contact,
};

/** How each record is titled in the form. */
export const RECORD_SECTION_TITLES: Record<CrmRecordKind, string> = {
  lead: "Lead",
  bid: "Bid",
  contact: "Contact",
};

/**
 * The anchor each section carries, so a surface that found no mapping can send the user to the one
 * they need rather than the top of a page with three on it. The router scrolls to a matching id.
 */
export function fieldMappingSectionId(kind: CrmRecordKind): string {
  return `field-mapping-${kind}`;
}

/** The records the form configures, in the order they are shown. */
export const MAPPABLE_RECORD_KINDS: CrmRecordKind[] = ["lead", "bid", "contact"];
